/**
 * 承認待ち。hr:approver が承認・却下する(自分が出した申請は承認できない)。
 *
 * 一覧テンプレート。ビュー: 自分が承認者(未決で、自分が出していない・まだ押していないもの)/ すべて。
 * 承認 / 却下は行に残し、行を押すと右パネル(概要 + 承認ルート)。
 * サーバーに「承認者で絞る」は無いので、未決(pending)を 200 件まで読んで手元で除く。
 */
import { useQueries } from "@tanstack/react-query";
import { Alert, Button, Space, Tag } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import type { WfRequest } from "../../lib/api";
import { useApi } from "../../lib/useApi";
import {
  DataTable,
  FilterBar,
  PageHeader,
  PARTIAL_CLEAR_LABEL,
  PartialResultNote,
  partialResultText,
  TimeAgo,
  multiSelectCondition,
  useSearchParam,
  useSearchParamInput,
  useSearchParamsPatch,
  type DataColumn,
} from "../../ui";
import { RequestPanel } from "./RequestPanel";
import { REQUEST_STATUSES, WF_KINDS, kindLabel, statusLabel } from "./constants";
import { errorText } from "./hooks";
import { ApproveReject, SlaTag } from "./wfActions";
import { employeeLink, matchesAll, requestStatusTag } from "./wfBits";
import { isOverdue, useRequests, useWfPerms } from "./wfHooks";

const LOCAL_LIMIT = "200";

/** 自分が押せる申請か(自分が出したものは不可、もう押したものは不要、承認者が指定されていれば
 *  自分のメールがそこに入っているもの)。 */
function waitingFor(me: { username: string; email: string } | undefined, r: WfRequest): boolean {
  if (r.status !== "pending" || !me) return false;
  if (r.requested_by === me.username || r.approved_by.includes(me.username)) return false;
  const emails = r.approver?.emails ?? [];
  return emails.length === 0 || emails.includes((me.email ?? "").toLowerCase());
}

export function ApprovalsPage() {
  const api = useApi();
  const { canApprove, me } = useWfPerms();
  const username = me?.username;
  const who = useMemo(() => (me ? { username: me.username, email: me.email } : undefined), [me]);
  const [sp] = useSearchParams();
  const patch = useSearchParamsPatch();
  const [panelId, setPanelId] = useSearchParam("panel");
  const [qInput, setQInput] = useSearchParamInput("q");
  const q = sp.get("q") ?? "";
  const viewParam = sp.get("view");
  const view: "mine" | "all" = viewParam === "all" || viewParam === "mine" ? viewParam : canApprove ? "mine" : "all";
  const csv = (name: string) => (sp.get(name) ?? "").split(",").filter(Boolean);
  const statuses = csv("status");
  const kinds = csv("kind");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  // 「自分が承認者」は手元で絞る。検索語・複数選択もサーバーに無いので同じ経路
  const local = view === "mine" || Boolean(q) || statuses.length > 1 || kinds.length > 1;
  const filters: Record<string, string> = {
    ...(view === "mine" ? { status: "pending" } : statuses.length === 1 ? { status: statuses[0] } : {}),
    ...(kinds.length === 1 ? { kind: kinds[0] } : {}),
  };
  const query = useRequests(
    local
      ? { ...filters, limit: LOCAL_LIMIT, offset: "0" }
      : { ...filters, limit: String(pageSize), offset: String((page - 1) * pageSize) },
  );
  const counts = useQueries({
    queries: [
      {
        queryKey: ["hr", "wf-requests", "count", "mine", username, me?.email],
        queryFn: () => api.wfRequests(new URLSearchParams({ limit: LOCAL_LIMIT, offset: "0", status: "pending" })),
        select: (d: { items: WfRequest[] }) => d.items.filter((r) => waitingFor(who, r)).length,
        staleTime: 60_000,
        enabled: !!username,
      },
      {
        queryKey: ["hr", "wf-requests", "count", "all"],
        queryFn: () => api.wfRequests(new URLSearchParams({ limit: "1", offset: "0" })),
        select: (d: { total: number }) => d.total,
        staleTime: 60_000,
      },
    ],
  });

  const filterKey = `${view}|${q}|${statuses}|${kinds}`;
  useEffect(() => setPage(1), [filterKey]);

  const rows = useMemo(() => {
    const items = query.data?.items ?? [];
    if (!local) return items;
    return items.filter(
      (r) =>
        (view !== "mine" || waitingFor(who, r)) &&
        (view === "mine" || statuses.length < 2 || statuses.includes(r.status)) &&
        (kinds.length < 2 || kinds.includes(r.kind)) &&
        matchesAll(q, r.id, r.employee_name, r.kind_label, r.template_name, r.requested_by, r.start_date),
    );
  }, [query.data, local, view, who, statuses, kinds, q]);
  const total = local ? rows.length : (query.data?.total ?? 0);
  // 手元で絞っていて、取得が上限で切れている(これより古い申請は対象外。fix1 中4)
  const fetched = query.data?.items.length ?? 0;
  const partial = local && (query.data?.total ?? 0) > Number(LOCAL_LIMIT);

  // 日付ありでも計902px。氏名12文字(約168px)と在籍タグは上下に分ける。
  const columns: DataColumn<WfRequest>[] = [
    {
      title: "#",
      ellipsis: false,
      dataIndex: "id",
      width: 110,
      // 申請番号は右パネルを開くボタン(行クリックと併存。Tab → Enter でも開ける。fix1 中5)
      render: (v: number) => (
        <button type="button" className="ui-link-btn ui-num" aria-label={`申請 #${v} の明細を開く`} onClick={() => setPanelId(String(v))}>
          {v}
        </button>
      ),
    },
    {
      title: "申請",
      ellipsis: false,
      width: 200,
      render: (_: unknown, r) => (
        <div>
          <div>
            {kindLabel(r.kind)}
            {r.template_name && <span className="ui-cell-sub">{r.template_name}</span>}
          </div>
        </div>
      ),
    },
    {
      title: "対象者",
      ellipsis: false,
      width: 200,
      render: (_: unknown, r) => (
        <div>
          <div className="ui-cell-main" title={r.employee_name}>
            {employeeLink(r.employee_id, r.employee_name)}
          </div>
          {r.employee_status && (
            <div className="ui-cell-sub"><Tag>{statusLabel(r.employee_status)}</Tag></div>
          )}
        </div>
      ),
    },
    { title: "日付", ellipsis: false, dataIndex: "start_date", width: 120 },
    {
      title: "状態",
      ellipsis: false,
      width: 200,
      render: (_: unknown, r) => (
        <Space size={4} wrap>
          {requestStatusTag(r)}
          <SlaTag sla={r.sla} />
        </Space>
      ),
    },
    {
      title: "承認",
      ellipsis: false,
      width: 240,
      render: (_: unknown, r) => (
        <Space size={4} wrap>
          <span className="ui-num">{r.approvals}</span>
          {r.approved_by.map((a) => (
            <Tag key={a}>{a}</Tag>
          ))}
        </Space>
      ),
    },
    {
      title: "申請者",
      ellipsis: false,
      width: 160,
      render: (_: unknown, r) => (
        <div>
          <div>{r.requested_by}</div>
          <div className="ui-cell-sub">
            <TimeAgo iso={r.created_at} />
          </div>
        </div>
      ),
    },
    { title: "操作", ellipsis: false, width: 180, render: (_: unknown, r) => <ApproveReject req={r} /> },
  ];

  const setCsv = (name: string) => (vals: string[]) => patch({ [name]: vals.length ? vals.join(",") : null }, true);
  const conditions = [
    multiSelectCondition({ key: "kind", label: "種別", options: WF_KINDS, selected: kinds, onChange: setCsv("kind") }),
    ...(view === "all"
      ? [multiSelectCondition({ key: "status", label: "状態", options: REQUEST_STATUSES, selected: statuses, onChange: setCsv("status") })]
      : []),
  ];
  const filtered = Boolean(q) || conditions.some((c) => c.value);
  const clearAll = () => {
    setQInput("");
    patch({ q: null, status: null, kind: null }, true);
  };
  // 手元の条件を全部外してサーバー側のページングに戻す(「自分が承認者」も手元で絞るので「すべて」へ)
  const clearLocal = () => {
    setQInput("");
    patch({ q: null, status: null, kind: null, view: "all" }, true);
  };
  const panelReq = panelId ? rows.find((r) => r.id === Number(panelId)) : undefined;

  return (
    <div className="ui-list">
      <div className="ui-list-main">
        <PageHeader
          title="承認待ち"
          description={
            <>
              承認は hr:approver。申請した本人は承認できない(職務分掌)。必要承認数は設定 wf.required_approvals
              で決まり、同じ人は何度押しても 1 票。
              {!canApprove && me && "(あなたは承認できないロールなので閲覧だけ)"}
            </>
          }
        />
        <FilterBar
          search={{ value: qInput, onChange: setQInput, placeholder: "対象者 / テンプレート / 申請者" }}
          views={{
            items: [
              { key: "mine", label: "自分が承認者", count: counts[0].data, tone: counts[0].data ? "warning" : undefined },
              { key: "all", label: "すべて", count: counts[1].data },
            ],
            active: view,
            onChange: (key) => patch({ view: key, status: null }, true),
          }}
          conditions={conditions}
          right={
            filtered ? (
              <Button size="small" onClick={clearAll}>
                条件を外す
              </Button>
            ) : undefined
          }
        />
        {query.isError && (
          <Alert type="error" showIcon message="申請を取得できなかった" description={errorText(query.error)} />
        )}
        {partial && <PartialResultNote limit={Number(LOCAL_LIMIT)} onClear={clearLocal} />}
        <DataTable<WfRequest>
          columns={columns}
          scrollX={columns.reduce((sum, column) => sum + column.width, 0)}
          rows={rows}
          rowKey={(r) => r.id}
          loading={query.isFetching}
          total={total}
          countLabel={partial ? `${fetched} 件中 ${rows.length} 件` : undefined}
          page={page}
          pageSize={pageSize}
          onPage={(p, s) => {
            setPage(p);
            setPageSize(s);
          }}
          selectedKey={panelId ? Number(panelId) : null}
          onRowClick={(row) => setPanelId(String(row.id))}
          rowTone={(row) => (isOverdue(row) ? "danger" : undefined)}
          error={query.isError ? "取得できなかったので表示できない" : undefined}
          empty={{
            text: `${filtered ? "条件に合う申請はない" : view === "mine" ? "あなたの承認を待っている申請はない" : "申請はない"}${
              partial ? `(${partialResultText(Number(LOCAL_LIMIT))})` : ""
            }`,
            action: partial ? (
              <Button onClick={clearLocal}>{PARTIAL_CLEAR_LABEL}</Button>
            ) : filtered ? (
              <Button onClick={clearAll}>条件を外す</Button>
            ) : undefined,
          }}
        />
      </div>
      {panelId && <RequestPanel id={Number(panelId)} req={panelReq} onClose={() => setPanelId(null)} />}
    </div>
  );
}
